/**
 * Demo-mode swap venue. Prices the stock from a small static table with a
 * plausible spread so portfolio maths is exercised realistically.
 */
import type { Hex } from "../types";
import type { SwapAdapter, SwapQuote, SwapResult } from "./types";

/** USD price per share, cents. Demo values only. */
const DEMO_PRICES_CENTS: Record<string, number> = {
  NVDA: 18_450,
  AAPL: 23_120,
  TSLA: 32_800,
  SPY: 62_300,
};

const SPREAD_BPS = 25n; // 0.25%
const STOCK_DECIMALS = 18;

function pseudoTxHash(seed: string): Hex {
  let h = 0n;
  for (const c of seed) h = (h * 131n + BigInt(c.charCodeAt(0))) % (1n << 256n);
  return `0x${h.toString(16).padStart(64, "0")}` as Hex;
}

export function demoStockPriceCents(symbol: string): number {
  return DEMO_PRICES_CENTS[symbol] ?? 10_000;
}

export class MockSwap implements SwapAdapter {
  name = "demo-swap";
  private inFlight = new Map<Hex, bigint>();

  async getQuote(stockSymbol: string, inputUnits: bigint): Promise<SwapQuote> {
    const feeUnits = (inputUnits * SPREAD_BPS) / 10_000n;
    const effective = inputUnits - feeUnits; // USDG 6dp
    const priceCents = BigInt(demoStockPriceCents(stockSymbol));
    // shares(18dp) = usd(6dp) * 1e18 / (priceCents/100 * 1e6)
    const expectedStockUnits = (effective * 10n ** BigInt(STOCK_DECIMALS) * 100n) / (priceCents * 1_000_000n);
    return {
      stockSymbol,
      inputUnits,
      expectedStockUnits,
      feeUnits,
      quoteId: `demo-${stockSymbol}-${inputUnits}`,
      expiresAt: Date.now() + 60_000,
    };
  }

  async submit(quote: SwapQuote): Promise<Hex> {
    const txHash = pseudoTxHash(`swap:${quote.quoteId}:${Date.now()}`);
    this.inFlight.set(txHash, quote.expectedStockUnits);
    return txHash;
  }

  async resolve(txHash: Hex, _stockSymbol: string, fallbackExpected?: bigint): Promise<SwapResult> {
    const stored = this.inFlight.get(txHash);
    if (stored === undefined && fallbackExpected === undefined) {
      throw new Error("Demo swap state lost (reload) — retry the round-up");
    }
    await new Promise((r) => setTimeout(r, 900));
    this.inFlight.delete(txHash);
    return { txHash, stockUnitsReceived: stored ?? fallbackExpected! };
  }
}
